import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as S from './policy.styles';
import imgUrbanDark from '../../../../assets/PNG/urbanCompass_dark.png';
import imgUrbanLight from '../../../../assets/PNG/urbanCompass_light.png';
import traditionalFlag from '../../../../assets/PNG/traditionalFlag.png';
import { getCurrentYear } from '../../../../helper/validationHelper';

const PolicyPage = ({ isDark, toggleTheme }) => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <S.PageWrapper>
      {/* Navbar */}
      <S.Navbar>
        <S.NavContent>
          <div>
            <Link to="/">
              {isDark ? (
                <S.Logo src={imgUrbanDark} alt="logo" />
              ) : (
                <S.Logo src={imgUrbanLight} alt="logo" />
              )}
            </Link>
          </div>
          <S.NavContent>
            <S.ThemeToggleButton onClick={toggleTheme}>
              <S.LightModeIcon
                $isDark={isDark}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 -960 960 960"
              >
                <path d="M480-360q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35Zm0 80q-83 0-141.5-58.5T280-480q0-83 58.5-141.5T480-680q83 0 141.5 58.5T680-480q0 83-58.5 141.5T480-280ZM200-440H40v-80h160v80Zm720 0H760v-80h160v80ZM440-760v-160h80v160h-80Zm0 720v-160h80v160h-80ZM256-650l-101-97 57-59 96 100-52 56Zm492 496-97-101 53-55 101 97-57 59Zm-98-550 97-101 59 57-100 96-56-52ZM154-212l101-97 55 53-97 101-59-57Zm326-268Z" />
              </S.LightModeIcon>
              <S.DarkModeIcon
                $isDark={isDark}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 -960 960 960"
              >
                <path d="M480-120q-150 0-255-105T120-480q0-150 105-255t255-105q14 0 27.5 1t26.5 3q-41 29-65.5 75.5T444-660q0 90 63 153t153 63q55 0 101-24.5t75-65.5q2 13 3 26.5t1 27.5q0 150-105 255T480-120Zm0-80q88 0 158-48.5T740-375q-20 5-40 8t-40 3q-123 0-209.5-86.5T364-660q0-20 3-40t8-40q-78 32-126.5 102T200-480q0 116 82 198t198 82Zm-10-270Z" />
              </S.DarkModeIcon>
            </S.ThemeToggleButton>{' '}
            <div>
              <S.SignInButton onClick={handleSignIn}>Sign In</S.SignInButton>
            </div>
          </S.NavContent>
        </S.NavContent>
      </S.Navbar>

      {/* Header */}
      <S.HeaderSection>
        <S.HeaderContent>
          <S.HeaderTitle>Privacy Policy</S.HeaderTitle>
        </S.HeaderContent>
      </S.HeaderSection>

      {/* Content */}
      <S.ContentSection>
        <div>
          <S.ContentBlock>
            <S.LastUpdated>
              <strong>Last updated</strong>: 01st January 2025
            </S.LastUpdated>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>1. Introduction</S.SectionTitle>
            <S.SectionParagraph>
              At Urban Compass Software, we are committed to protecting your privacy and handling
              your personal information in a transparent, secure, and responsible manner. This
              Privacy Policy explains how we collect, use, store, and protect your personal
              information when you interact with our website and services.
            </S.SectionParagraph>
            <S.SectionParagraph>
              By using our website, you agree to the practices described in this policy.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>2. Information We Collect</S.SectionTitle>
            <S.SectionParagraph>
              We collect personal information only where it is reasonably necessary for our business
              operations. This information may be collected:
            </S.SectionParagraph>
            <S.List>
              <li>Directly from you when you contact us, submit forms, or use our services</li>
              <li>From authorised representatives acting on your behalf</li>
              <li>From publicly available sources</li>
            </S.List>
            <S.SectionParagraph>
              The types of information we collect may include your name, contact details, business
              information, and any other information you choose to provide.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>3. How We Use Your Information</S.SectionTitle>
            <S.SectionParagraph>We use your personal information to:</S.SectionParagraph>
            <S.List>
              <li>Deliver and improve our products and services</li>
              <li>Respond to enquiries and provide customer support</li>
              <li>Communicate important updates or service-related information</li>
              <li>Send marketing communications where you have opted in</li>
              <li>Comply with legal and regulatory obligations</li>
            </S.List>
            <S.SectionParagraph>
              We only use personal information for the purposes for which it was collected, or for
              closely related purposes you would reasonably expect.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>4. Marketing Communications</S.SectionTitle>
            <S.SectionParagraph>
              Where you have subscribed or expressed interest, we may send you information about
              products, services, or updates we believe may be relevant to you. <br />
              You can opt out of marketing communications at any time by:
            </S.SectionParagraph>
            <S.List>
              <li>Using the unsubscribe link in our emails, or</li>
              <li>Contacting us at marketing@urbancompasssoftware.com</li>
            </S.List>
            <S.SectionParagraph>
              We may also share limited information with carefully selected third-party partners for
              marketing purposes. These partners are required to handle your information securely
              and in accordance with applicable privacy laws.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>5. Publicly Shared Information</S.SectionTitle>
            <S.SectionParagraph>
              Any information you choose to make publicly available on our website (such as blog
              comments or testimonials) may be visible to other users. Please note that even if you
              remove this content, copies may still exist in cached pages or third-party archives
              beyond our control.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>6. Data Security</S.SectionTitle>
            <S.SectionParagraph>
              We take reasonable and appropriate measures to protect your personal information from
              loss, misuse, unauthorised access, modification, or disclosure. In the unlikely event
              of a data breach that may compromise your personal information, we will notify
              affected individuals and relevant authorities in accordance with applicable laws. When
              personal information is no longer required, we securely delete or destroy it.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>7. Cookies and Tracking Technologies</S.SectionTitle>
            <S.SectionParagraph>
              Our website uses cookies and similar technologies to enhance your browsing experience,
              analyse website traffic, and support marketing activities.
            </S.SectionParagraph>
            <S.SectionParagraph>
              Cookies do not access personal information directly but help us understand how users
              interact with our website. We may also use third-party services such as Google Ads and
              Facebook Advertising, which may place cookies or tracking pixels to deliver relevant
              advertisements.
            </S.SectionParagraph>
            <S.SectionParagraph>
              You can manage or disable cookies through your browser settings. Please note that
              disabling cookies may affect certain website features.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>8. Disclosure of Personal Information</S.SectionTitle>
            <S.SectionParagraph>
              We do not sell personal information. <br />
              We may disclose personal information:
            </S.SectionParagraph>
            <S.List>
              <li>Where required or authorised by law</li>
              <li>
                To protect the rights, property, or safety of Urban Compass Software, our users, or
                third parties
              </li>
              <li>
                As part of a business transaction such as a merger, acquisition, or asset sale,
                subject to appropriate confidentiality obligations
              </li>
            </S.List>
            <S.SectionParagraph>
              Any disclosure is made in good faith and in compliance with applicable privacy laws.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>9. Third-Party Websites</S.SectionTitle>
            <S.SectionParagraph>
              Our website may contain links to external websites operated by third parties. We are
              not responsible for the privacy practices or content of those websites and encourage
              you to review their privacy policies separately.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>10. Your Rights and Choices</S.SectionTitle>
            <S.SectionParagraph>You have the right to:</S.SectionParagraph>
            <S.List>
              <li>Access the personal information we hold about you</li>
              <li>Request corrections to inaccurate or outdated information</li>
              <li>Withdraw consent for marketing communications at any time</li>
            </S.List>
            <S.SectionParagraph>
              To make a request, please contact us using the details below.
            </S.SectionParagraph>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>11. Contact Us</S.SectionTitle>
            <S.SectionParagraph>
              If you have any questions, concerns, or requests regarding this Privacy Policy or how
              we handle personal information, please contact us:
            </S.SectionParagraph>
            <S.ContactInfo>
              <div>Urban Compass Software</div>
              <div>
                <strong>Phone:</strong> 1300 100 436
              </div>
              <div>
                <strong>Email:</strong> info@urbancompasssoftware.com
              </div>
            </S.ContactInfo>
          </S.ContentBlock>

          <S.ContentBlock>
            <S.SectionTitle>12. Changes to This Privacy Policy</S.SectionTitle>
            <S.SectionParagraph>
              We may update this Privacy Policy from time to time to reflect changes in our
              practices or legal requirements. Updates take effect immediately upon publication on
              our website. We encourage you to review this policy periodically to stay informed.
            </S.SectionParagraph>
          </S.ContentBlock>
        </div>
      </S.ContentSection>

      {/* Acknowledgement */}
      <S.AcknowledgementSection>
        <S.AcknowledgementText>
          In the spirit of reconciliation, The Urban Compass Software Pty Ltd acknowledges the
          Traditional Custodians of country throughout Australia and their connections to land, sea
          and community. We pay our respect to their Elders past and present and extend that respect
          to all First Nations peoples today with disability, their families, and their carers.
        </S.AcknowledgementText>
        <S.AcknowledgementImage>
          <img src={traditionalFlag} alt="traditionalFlag" />
        </S.AcknowledgementImage>
      </S.AcknowledgementSection>

      {/* Footer */}
      <S.Footer>
        <S.FooterWrapper>
          <S.FooterLeftSection>
            <S.FooterText>
              © {getCurrentYear()}{' '}
              <S.FooterLink href="https://urbancompasssoftware.com/" target="_blank">
                Urban Compass
              </S.FooterLink>
              . All rights reserved.
            </S.FooterText>
            <S.FooterPolicies>
              <Link to="/terms">Terms and Conditions</Link> |{' '}
              <Link to="/policy">Privacy Policy</Link>
            </S.FooterPolicies>
          </S.FooterLeftSection>

          <S.FooterCenterSection>
            <S.SocialLinks>
              <S.SocialIcon
                href="https://www.linkedin.com/company/urban-compass-software/?viewAsMember=true"
                target="_blank"
                aria-label="LinkedIn"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect width="4" height="12" x="2" y="9" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </S.SocialIcon>
              <S.SocialIcon
                href="https://www.youtube.com/@UrbanCompassSoftware"
                target="_blank"
                aria-label="YouTube"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                  <path d="m10 15 5-3-5-3z" />
                </svg>
              </S.SocialIcon>
              <S.SocialIcon href="#" aria-label="Instagram">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                </svg>
              </S.SocialIcon>
            </S.SocialLinks>
          </S.FooterCenterSection>
        </S.FooterWrapper>
      </S.Footer>
    </S.PageWrapper>
  );
};

export default PolicyPage;
